import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { UtilityService } from '../shared/utility.service';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { startWith, Subscription } from 'rxjs';
import { capacityObject } from '../shared/auth/auth.service';
import { Location, Bundle } from 'fhir/r4';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../shared/auth/auth.service';
import { StateService } from '../shared/state.service';
import { ChipsDirective } from '../chips.directive';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { CodeableConcept2Pipe } from '../shared/codeable-concept2.pipe';
import { NaPipe } from '../shared/na.pipe';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { MatDialog } from '@angular/material/dialog';
import { DetailzViewzComponent } from '../detailz-viewz/detailz-viewz.component';
import { DetailsBuilderObject } from '../detailz-viewz/details-builder.service';
import { ChangeLocationStatusDialogComponent } from './change-location-status-dialog/change-location-status-dialog.component';
import { QuickAddLocationComponent } from './quick-add-location/quick-add-location.component';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { LocationSearchService, LocationRow } from './location-search.service';
import { TableFilters } from '../shared/table-filtering.service';

@Component({
  selector: 'app-admission-location',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    MatPaginatorModule,
    MatProgressBarModule,
    ReactiveFormsModule,
    DatePipe,
    TitleCasePipe,
    ChipsDirective,
    EmptyStateComponent,
    TableHeaderComponent,
    CodeableConcept2Pipe,
    NaPipe,
    ReferenceDisplayDirective,
    EmphasisDirective
  ],
  templateUrl: './admission-location.component.html',
  styleUrls: ['../patient-observation/patient-observation.component.scss', './admission-location.component.scss'],
})
export class AdmissionLocationComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly stateService = inject(StateService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly utilityService = inject(UtilityService);
  private readonly subs = new Subscription();
  private filterSubs = new Subscription();

  // ─── Search service ────────────────────────────────────────────────────────
  readonly locationSearch = inject(LocationSearchService);
  readonly loading$ = this.locationSearch.fetching$;
  readonly total$ = this.locationSearch.total$;
  readonly error$ = this.locationSearch.error$;

  displayedColumns: string[] = ['name', 'status', 'physicalType', 'occupancyStatus', 'availableBeds', 'meta', 'actions'];

  /**
   * Single source of truth for all enriched Location resources.
   * Keyed by Location ID so that StateService real-time upserts
   * can merge into the set fetched by fetchAll().
   */
  private allLocationsMap = new Map<string, Location>();

  /** Last enriched + transformed rows — reused by recomputeSideFilters to avoid double-enrichment. */
  private cachedRows: LocationRow[] = [];

  /** Map of Location/ID -> Occupant name and ID */
  private activeEncountersMap = new Map<string, string>();

  // ─── Current active filters ───────────────────────────────────────────────
  private activeFilters: TableFilters = {};

  // ─── Tab filter ───────────────────────────────────────────────────────────
  physicalTypeFilter = new FormControl<string>('ward');

  // ─── Ward chip filter (Rooms tab) ─────────────────────────────────────────
  availableWards: string[] = [];
  selectedWardChip: string | null = null;

  // ─── Table-header checkbox filters (Beds tab) ─────────────────────────────
  locationTableFilterArray = new Map<string, any[]>();
  locationFiltersFormControlObject: any = {};

  // ─── Searchable fields for app-table-header ───────────────────────────────
  searchableFields: { label: string; value: string }[] = [
    { label: 'Name', value: '_search' },
  ];
  selectedSearchField = '_search';

  // ─── Pagination ───────────────────────────────────────────────────────────
  pageSize = 50;
  paginatorPageIndex = 0;

  // ─── Details view builder ─────────────────────────────────────────────────
  locationDetailsBuilder: DetailsBuilderObject = {
    resourceName: 'Location',
    resourceIcon: 'location_on',
    specialHeader: {
      strongSectionKey: 'name',
      iconSectionKeys: ['status', 'operationalStatus'],
      contentSectionKeys: ['physicalType', 'partOf']
    },
    groups: [
      {
        groupName: 'Basic Information',
        groupIcon: 'info',
        groupKeys: ['name', 'status', 'description', 'businessIdentifier', 'availableBeds', 'occupant', 'belongsTo']
      },
      {
        groupName: 'Classification',
        groupIcon: 'category',
        groupKeys: ['physicalType', 'type', 'partOf']
      }
    ]
  };

  ngOnInit(): void {
    // 1. Bootstrap the search service in local-filtering mode
    this.locationSearch.initLocal();

    // Fetch active encounters and map them to bed IDs silently
    const orgId = this.auth.user.getValue()?.organizationId || '';
    if (orgId) {
      this.subs.add(
        this.utilityService.threeTierQueryAndSyncToStateOnce$<any>({
          resourceType: 'Encounter',
          urlSearchObject: {
            _tag: orgId,
            status: 'in-progress',
            _include: 'Encounter:subject'
          },
          count: 0,
          tier1FollowNextLinks: true,
          tier1MaxPages: 100
        }).subscribe({
          next: (resources) => {
            const patients = resources.filter((e: any) => e.resourceType === 'Patient');
            const encounters = resources.filter((e: any) => e.resourceType === 'Encounter');

            encounters.forEach((encounter: any) => {
              const locRef = encounter.location?.[0]?.location?.reference;
              const patientRef = encounter.subject?.reference;

              if (locRef && patientRef) {
                const patient = patients.find((p: any) => `Patient/${p.id}` === patientRef);
                if (patient) {
                  const nameStr = patient.name?.[0]?.text || `${patient.name?.[0]?.given?.[0] || ''} ${patient.name?.[0]?.family || ''}`.trim() || 'Unknown Patient';
                  const pid = patient.identifier?.find((i: any) => i.system?.includes('/pid'))?.value || patient.id;
                  this.activeEncountersMap.set(locRef, `${nameStr} (ID: ${String(pid).toUpperCase()})`);
                }
              }
            });
            // Re-render if locations are already loaded
            if (this.allLocationsMap.size > 0) {
              this.rebuildAndPush();
            }
          },
          error: (err) => console.error('Failed to fetch encounters for location details', err)
        })
      );
    }

    // 2. Smart fetch: compare StateService cached locations with FHIR total count.
    //    If StateService has all data, skip extra HTTP calls; otherwise fetch missing pages.
    this.locationSearch.ensureAllLocationsLoaded((allLocations: Location[]) => {
      // Rebuild map with everything fetched so far
      allLocations.forEach(loc => {
        if (loc?.id && loc?.name) {
          this.allLocationsMap.set(loc.id, loc);
          // Sync each fetched location to StateService so stateService also has the complete set
          this.stateService.persistOrgWideResource(loc, 'saved');
        }
      });
      this.rebuildAndPush();
    });

    // 3. Subscribe to StateService for real-time upserts (newly saved locations).
    //    Merge them into the map so they appear immediately without re-fetching.
    //    GUARD: Skip rebuilds while fetchAll is still paging — the fetch callback handles progressive rebuilds.
    this.subs.add(
      this.stateService.orgWideResources.locations.subscribe((entries: any[]) => {
        // While fetch is in progress, the fetchAll callback already drives rebuilds.
        // Reacting to StateService here would cause redundant rebuilds with incomplete data.
        if (this.locationSearch.fetching$.getValue()) return;

        let hasNew = false;
        (entries ?? []).forEach(entry => {
          // Safely unwrap location whether entry is { actualResource: loc }, { resource: loc }, or Location object
          const loc = entry?.actualResource ?? entry?.resource ?? (entry?.resourceType === 'Location' ? entry : null);
          if (!loc?.id || !loc?.name) return;

          const existing = this.allLocationsMap.get(loc.id);
          const existingTs = existing?.meta?.lastUpdated ?? '';
          const incomingTs = loc.meta?.lastUpdated ?? '';

          if (!existing || incomingTs >= existingTs) {
            this.allLocationsMap.set(loc.id, loc as Location);
            hasNew = true;
          }
        });

        if (hasNew) {
          this.rebuildAndPush();
        }
      })
    );

    // 4. React to tab changes
    this.subs.add(
      this.physicalTypeFilter.valueChanges.pipe(startWith('ward')).subscribe(() => {
        this.selectedWardChip = null;
        this.recomputeSideFilters();
        this.pushFilters();
      })
    );
  }

  // ─── Called after allLocationsMap changes ─────────────────────────────────

  private rebuildAndPush(): void {
    // Convert map values → array, sort newest-first
    const sorted = Array.from(this.allLocationsMap.values()).sort((a, b) => {
      const ta = a.meta?.lastUpdated ?? '';
      const tb = b.meta?.lastUpdated ?? '';
      return tb.localeCompare(ta);
    });

    const enriched = this.enrichLocations(sorted);
    this.cachedRows = enriched.map(loc => this.locationSearch.transform(loc));

    console.log(`[AdmissionLocation] Pushing ${this.cachedRows.length} enriched rows to table. Sample row:`, {
      id: this.cachedRows[0]?.id,
      name: this.cachedRows[0]?.name,
      status: this.cachedRows[0]?.status,
      physicalTypeCode: this.cachedRows[0]?.physicalTypeCode,
      availableBeds: this.cachedRows[0]?.availableBeds,
      Ward: this.cachedRows[0]?.Ward,
      Room: this.cachedRows[0]?.Room
    });

    this.locationSearch.setLocalRows(this.cachedRows);
    this.recomputeSideFilters();
  }

  // ─── Search bar callback ───────────────────────────────────────────────────
  onSearchCriteriaChanged(criteria: { term: string; field: string | null }): void {
    const term = (criteria.term ?? '').trim();
    const field = criteria.field ?? '_search';

    if (term) {
      this.activeFilters = { ...this.activeFilters, [field]: term };
    } else {
      const copy = { ...this.activeFilters };
      delete copy[field];
      this.activeFilters = copy;
    }
    this.pushFilters();
  }

  // ─── Tab button click ─────────────────────────────────────────────────────
  setPhysicalTypeFilter(value: string): void {
    if (this.physicalTypeFilter.value !== value) {
      this.selectedWardChip = null;
    }
    this.physicalTypeFilter.setValue(value);
  }

  // ─── Ward chip click ──────────────────────────────────────────────────────
  selectWardChip(ward: string): void {
    this.selectedWardChip = this.selectedWardChip === ward ? null : ward;
    this.pushFilters();
  }

  // ─── Pagination ───────────────────────────────────────────────────────────
  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.paginatorPageIndex = event.pageIndex;
    this.locationSearch.changePage(event.pageIndex);
  }

  // ─── Actions ──────────────────────────────────────────────────────────────
  onExportLocations(): void {
    console.log('Export payload:', this.locationSearch.dataSource.data);
  }

  onAddAdmissionLocation(): void {
    this.router.navigate(['/app/admission-location/add']);
  }

  showRow(location: LocationRow): void {
    this.showLocationDetails(location);
  }

  showLocationDetails(location: LocationRow): void {
    const resource = (location.rawResource ?? location) as any;
    const excludeKeys = this.computeEmptyKeys(resource);

    const actionButtons: any[] = [
      {
        key: 'change_status',
        label: 'Change Status',
        icon: 'swap_horiz',
        color: 'primary',
        ignoreCapabilities: true
      }
    ];

    const typeCode = resource.physicalType?.coding?.[0]?.code?.toLowerCase() || '';
    const typeDisplay = resource.physicalType?.text?.toLowerCase() || '';

    if (typeCode === 'wa' || typeDisplay === 'ward') {
      actionButtons.push({
        key: 'add_child',
        label: 'Add Room',
        icon: 'add',
        color: 'accent',
        ignoreCapabilities: true
      });
    } else if (typeCode === 'ro' || typeDisplay === 'room') {
      actionButtons.push({
        key: 'add_child',
        label: 'Add Bed',
        icon: 'add',
        color: 'accent',
        ignoreCapabilities: true
      });
    }

    const dialogRef = this.dialog.open(DetailzViewzComponent, {
      maxHeight: '93vh',
      maxWidth: '650px',
      width: '100%',
      data: {
        resourceData: resource,
        detailsBuilderObject: this.locationDetailsBuilder,
        excludeKeys,
        actionButtons
      }
    });

    this.subs.add(
      dialogRef.componentInstance.actionInvoked.subscribe((event: { key: string; resource?: any }) => {
        if (event.key === 'change_status') {
          dialogRef.close();
          this.changeLocationStatus(location);
        } else if (event.key === 'add_child') {
          dialogRef.close();
          const pType = (typeCode === 'wa' || typeDisplay === 'ward') ? 'Ward' : 'Room';
          const addDialog = this.dialog.open(QuickAddLocationComponent, {
            width: '600px',
            data: { parentId: resource.id, parentType: pType }
          });

          this.subs.add(
            addDialog.afterClosed().subscribe((res: any) => {
              if (res) {
                const actualLoc = res.resource || res;
                const bundle: Bundle = {
                  resourceType: 'Bundle',
                  type: 'transaction-response',
                  entry: [{ resource: actualLoc }]
                };
                this.stateService.processOrgWideBundleTransaction(bundle);
              }
            })
          );
        }
      })
    );
  }

  changeLocationStatus(location: LocationRow): void {
    const resource = (location.rawResource ?? location) as Location;
    this.dialog.open(ChangeLocationStatusDialogComponent, {
      width: '500px',
      data: { location: resource }
    });
  }

  // ─── Permissions ──────────────────────────────────────────────────────────
  private computePermissions(resource: keyof typeof capacityObject): void {}

  get canAddLocation(): boolean { return this.auth.can('location', 'add'); }
  get canExportLocation(): boolean { return this.auth.can('location', 'viewAll'); }

  // ─── Side filters (ward chips / header dropdowns) ─────────────────────────

  private recomputeSideFilters(): void {
    const tab = this.physicalTypeFilter.value ?? 'ward';

    // Use the already-enriched cached rows — no second enrichment needed
    const tabRows = this.cachedRows.filter(row => {
      if (tab === 'all') return true;
      const raw = row.rawResource as Location;
      const typeText = raw.physicalType?.text?.toLowerCase() ?? '';
      const matchesCode = (raw.physicalType?.coding ?? []).some(
        c => c.code?.toLowerCase() === tab || c.display?.toLowerCase() === tab
      );
      return typeText === tab || matchesCode;
    });

    // We want a 'Status' filter on all tabs
    const statusSet = new Set<string>();
    tabRows.forEach(row => {
      const raw = row.rawResource as Location;
      if (raw.status) {
        statusSet.add(raw.status);
        (row as any)['Status'] = raw.status; // Inject for filter mapping
      }
    });
    const sortedStatuses = Array.from(statusSet).sort();

    if (tab === 'room') {
      this.availableWards = [];
      const wardSet = new Set<string>();
      tabRows.forEach(row => { if (row['Ward']) wardSet.add(row['Ward'] as string); });
      const sortedWards = Array.from(wardSet).sort();
      console.log('[LocationFilters] Updated filters for Room tab');
      this.buildHeaderFilters(new Map([
        ['Status', sortedStatuses],
        ['Ward', sortedWards]
      ]));
    } else if (tab === 'bed') {
      this.availableWards = [];
      const occupancySet = new Set<string>();
      const roomSet = new Set<string>();
      const wardSet = new Set<string>();
      tabRows.forEach(row => {
        if (row['Occupancy Status']) occupancySet.add(row['Occupancy Status'] as string);
        if (row['Room']) roomSet.add(row['Room'] as string);
        if (row['Ward']) wardSet.add(row['Ward'] as string);
      });
      const sortedWards = Array.from(wardSet).sort();
      console.log('[LocationFilters] Updated filters for Bed tab');
      this.buildHeaderFilters(new Map([
        ['Status', sortedStatuses],
        ['Occupancy Status', Array.from(occupancySet).sort()],
        ['Room', Array.from(roomSet).sort()],
        ['Ward', sortedWards]
      ]));
    } else {
      this.availableWards = [];
      console.log('[LocationFilters] Updated filters for Ward tab');
      this.buildHeaderFilters(new Map([
        ['Status', sortedStatuses]
      ]));
    }
  }

  private buildHeaderFilters(filterMap: Map<string, any[]>): void {
    this.locationTableFilterArray = filterMap;
    this.locationFiltersFormControlObject = {};

    this.filterSubs.unsubscribe();
    this.filterSubs = new Subscription();

    for (const [key, values] of filterMap) {
      const group = new FormGroup({});
      for (const val of values) {
        group.addControl(val, new FormControl(false));
      }
      this.locationFiltersFormControlObject[key] = group;
      this.filterSubs.add(
        group.valueChanges.subscribe(() => this.onHeaderFilterChange())
      );
    }
  }

  private onHeaderFilterChange(): void {
    const updated: TableFilters = { ...this.activeFilters };
    for (const key of Object.keys(this.locationFiltersFormControlObject)) {
      const groupValue = this.locationFiltersFormControlObject[key].value as Record<string, boolean>;
      const selected = Object.keys(groupValue).filter(k => groupValue[k]);
      if (selected.length) {
        updated[key] = selected.join(',');
      } else {
        delete updated[key];
      }
    }
    this.activeFilters = updated;
    this.pushFilters();
  }

  private pushFilters(): void {
    const tab = this.physicalTypeFilter.value ?? 'all';
    const composed: TableFilters = { ...this.activeFilters, _physicalType: tab };
    this.locationSearch.search(composed);
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  private computeEmptyKeys(location: any): string[] {
    if (!location) return [];
    return Object.keys(location).filter(key => this.isEmptyValue(location[key]));
  }

  private isEmptyValue(value: any): boolean {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0 || value.every(v => this.isEmptyValue(v));
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  // ─── Location enrichment ──────────────────────────────────────────────────

  private enrichLocations(rawLocations: Location[]): Location[] {
    // Step 1: apply Ward name normalisation
    const locations = rawLocations.map(loc => {
      const cloned = { ...loc };
      const coding = cloned.physicalType?.coding || [];
      const text = cloned.physicalType?.text?.toLowerCase() || '';
      const isWard = coding.some(c =>
        ['wa', 'ward'].includes(c.code?.toLowerCase() || '') ||
        c.display?.toLowerCase().includes('ward')
      ) || text.includes('ward');

      if (isWard && cloned.name) {
        const name = cloned.name.trim();
        const hasWard = /\bward\b/i.test(name);
        if (!hasWard) {
          const isShortToken = /^([a-zA-Z]|\d+[a-zA-Z]?)$/.test(name);
          cloned.name = isShortToken ? `Ward ${name}` : `${name} Ward`;
        }
      } else {
        const isRoom = coding.some(c =>
          ['ro', 'room'].includes(c.code?.toLowerCase() || '') ||
          c.display?.toLowerCase().includes('room')
        ) || text.includes('room');

        if (isRoom && cloned.name) {
          const name = cloned.name.trim();
          const hasRoom = /\broom\b/i.test(name);
          if (!hasRoom) {
            const isShortToken = /^([a-zA-Z]|\d+[a-zA-Z]?)$/.test(name);
            cloned.name = isShortToken ? `Room ${name}` : `${name} Room`;
          }
        } else {
          const isBed = coding.some(c =>
            ['bd', 'bed'].includes(c.code?.toLowerCase() || '') ||
            c.display?.toLowerCase().includes('bed')
          ) || text.includes('bed');

          if (isBed && cloned.name) {
            const name = cloned.name.trim();
            const hasBed = /\bbed\b/i.test(name);
            if (!hasBed) {
              const isShortToken = /^([a-zA-Z]|\d+[a-zA-Z]?)$/.test(name);
              cloned.name = isShortToken ? `Bed ${name}` : `${name} Bed`;
            }
          }
        }
      }
      return cloned;
    });

    // Step 2: build reference lookup map
    const locationMap = new Map<string, Location>();
    locations.forEach(loc => {
      if (loc.id) locationMap.set(`Location/${loc.id}`, loc);
    });

    // Step 3: helper – is ancestorId an ancestor of descendent?
    const isAncestor = (ancestorId: string, descendent: Location): boolean => {
      let current = descendent;
      let depth = 0;
      while (current.partOf && depth < 10) {
        const rawRef = typeof current.partOf === 'string'
          ? current.partOf
          : current.partOf.reference;
        if (!rawRef) break;
        const ref = rawRef.startsWith('Location/') ? rawRef : `Location/${rawRef}`;
        if (ref === `Location/${ancestorId}`) return true;
        const parent = locationMap.get(ref);
        if (parent) current = parent; else break;
        depth++;
      }
      return false;
    };
    // Step 4: enrich each location with hierarchy + derived fields
    return locations.map(loc => {
      const enriched = { ...loc } as any;

      const locIdObj = loc.identifier?.find((i: any) => i.system === 'https://elikita-server.daalitech.com/fhir/identifier/loc');
      if (locIdObj && locIdObj.value) {
        enriched.businessIdentifier = locIdObj.value;
      }

      // ─── Single-pass ancestor traversal ───────────────────────────────────
      // Walk up the partOf chain, collect names AND identify each ancestor's
      // physical type so we can assign Room / Ward correctly regardless of
      // how many levels deep the hierarchy is.
      let current = loc;
      const hierarchyNames: string[] = [];
      let resolvedRoomName = '';
      let resolvedWardName = '';
      let depth = 0;

      while (current.partOf && depth < 10) {
        const rawRef = typeof current.partOf === 'string'
          ? current.partOf
          : current.partOf.reference;
        if (!rawRef) break;
        const ref = rawRef.startsWith('Location/') ? rawRef : `Location/${rawRef}`;
        const parent = locationMap.get(ref);

        if (parent) {
          hierarchyNames.push(parent.name || 'Unnamed');

          // Identify whether this ancestor is a Room or Ward by physicalType
          const pCoding = parent.physicalType?.coding || [];
          const pText = parent.physicalType?.text?.toLowerCase() || '';

          const parentIsRoom = pCoding.some(c =>
            c.code?.toLowerCase() === 'ro' || c.code?.toLowerCase() === 'room' ||
            c.display?.toLowerCase().includes('room')
          ) || pText.includes('room');

          const parentIsWard = pCoding.some(c =>
            ['wa', 'ward'].includes(c.code?.toLowerCase() || '') ||
            c.display?.toLowerCase().includes('ward')
          ) || pText.includes('ward');

          // Keep the NEAREST (first-found) room and ward going upward
          if (parentIsRoom && !resolvedRoomName) resolvedRoomName = parent.name || '';
          if (parentIsWard && !resolvedWardName) resolvedWardName = parent.name || '';

          current = parent;
        } else {
          // Ancestor not found in local map — use display hint if available
          if (typeof current.partOf !== 'string' && current.partOf.display) {
            hierarchyNames.push(current.partOf.display);
          }
          break;
        }
        depth++;
      }

      if (hierarchyNames.length) {
        enriched.belongsTo = hierarchyNames.join(', ');
        enriched._hierarchyString = hierarchyNames.join(', ');
      }

      // ── Physical type helpers ─────────────────────────────────────────────
      const coding = loc.physicalType?.coding || [];
      const text = loc.physicalType?.text?.toLowerCase() || '';
      const isBed = coding.some(c =>
        c.code?.toLowerCase() === 'bd' || c.code?.toLowerCase() === 'bed' ||
        c.display?.toLowerCase().includes('bed')
      ) || text.includes('bed');
      const isRoom = coding.some(c =>
        c.code?.toLowerCase() === 'ro' || c.code?.toLowerCase() === 'room' ||
        c.display?.toLowerCase().includes('room')
      ) || text.includes('room');
      const isWardOrRoom = coding.some(c =>
        ['wa', 'ro', 'ward', 'room'].includes(c.code?.toLowerCase() || '') ||
        c.display?.toLowerCase().includes('ward') ||
        c.display?.toLowerCase().includes('room')
      ) || text.includes('ward') || text.includes('room');

      if (isBed) {
        const opCode = (loc.operationalStatus?.code || '').toUpperCase();
        const opDisplay = loc.operationalStatus?.display || (loc.operationalStatus as any)?.text;
        
        let opStatusLabel = '';
        if (loc.status === 'suspended') {
          opStatusLabel = 'Suspended';
        } else if (loc.status === 'inactive') {
          opStatusLabel = 'Inactive';
        } else if (opDisplay) {
          opStatusLabel = opDisplay;
        } else if (opCode === 'O') {
          opStatusLabel = 'Occupied';
        } else if (opCode === 'C') {
          opStatusLabel = 'Closed';
        } else if (opCode === 'H') {
          opStatusLabel = 'Housekeeping';
        } else if (opCode === 'I') {
          opStatusLabel = 'Isolated';
        } else if (opCode === 'K') {
          opStatusLabel = 'Contaminated';
        } else {
          // Absence of operationalStatus prop = Unoccupied
          opStatusLabel = 'Unoccupied';
        }

        enriched['Occupancy Status'] = opStatusLabel;
        // Use type-identified ancestors — not positional indexing
        enriched['Room'] = resolvedRoomName;
        enriched['Ward'] = resolvedWardName;
        
        // Map the occupant if the bed is occupied
        const locRef = `Location/${loc.id}`;
        if (this.activeEncountersMap.has(locRef)) {
          enriched.occupant = this.activeEncountersMap.get(locRef);
        } else {
          enriched.occupant = 'N/A';
        }
      } else if (isRoom) {
        enriched['Ward'] = resolvedWardName;
      }

      // ── Available-beds counter ────────────────────────────────────────────
      if (loc.id) {
        let bedCount = 0;
        let totalBedCount = 0;
        locations.forEach(potentialBed => {
          const pbCoding = potentialBed.physicalType?.coding || [];
          const pbText = potentialBed.physicalType?.text?.toLowerCase() || '';
          const pbIsBed = pbCoding.some(c =>
            c.code?.toLowerCase() === 'bd' || c.code?.toLowerCase() === 'bed' ||
            c.display?.toLowerCase().includes('bed')
          ) || pbText.includes('bed');

          if (pbIsBed && isAncestor(loc.id!, potentialBed)) {
            totalBedCount++;
            const pbOpCode = (potentialBed.operationalStatus?.code || '').toUpperCase();
            const pbIsUnavailable = ['O', 'C', 'H', 'K', 'I'].includes(pbOpCode);
            if (potentialBed.status === 'active' && !pbIsUnavailable) {
              bedCount++;
            }
          }
        });
        if (totalBedCount > 0 || isWardOrRoom) {
          enriched.availableBeds = `${bedCount} / ${totalBedCount}`;
        }
      }

      return enriched as Location;
    });
  }

  ngOnDestroy(): void {
    this.locationSearch.destroy();
    this.subs.unsubscribe();
    this.filterSubs.unsubscribe();
  }
}
